import * as THREE from 'three';
import type { SimWorld, SurfaceName, Vec3 } from '@polyloop/sim';

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRACK REGISTRY                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

export interface TrackDef {
  id: string;
  name: string;
  biome: 'verdant' | 'tundra' | 'neon' | 'sandbox';
  difficulty: 1 | 2 | 3;
  blurb: string;
  build: (scene: THREE.Scene, sim: SimWorld) => void;
  waypoints: readonly Vec3[];
  spawns: readonly { position: Vec3; yaw: number }[];
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* GEOMETRY HELPERS                                                            */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Wedge ramp = a triangular prism with the slope rising from the +Z (player-facing)
 * edge to the -Z (back) edge. Use yaw to rotate which direction the ramp climbs.
 *
 * Local vertex layout (before world placement/rotation):
 *   v0 (-W/2, 0,  L/2)  front-left  (slope low end, player-facing)
 *   v1 ( W/2, 0,  L/2)  front-right
 *   v2 ( W/2, 0, -L/2)  back-right  (under the peak)
 *   v3 (-W/2, 0, -L/2)  back-left
 *   v4 (-W/2, H, -L/2)  peak-left
 *   v5 ( W/2, H, -L/2)  peak-right
 *
 * Only the slope face is exposed to oncoming cars; the bottom sits on the
 * ground and the back face is hidden against terrain in most placements.
 */
function addWedgeRamp(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: {
    position: Vec3;
    yawDeg: number;
    width: number;
    length: number;
    height: number;
    color: number;
    surface?: SurfaceName;
  },
): void {
  const { position, yawDeg, width: W, length: L, height: H, color, surface = 'road' } = opts;
  const yaw = THREE.MathUtils.degToRad(yawDeg);

  // Vertex buffer (xyz × 6).
  const verts = new Float32Array([
    -W / 2, 0,  L / 2, //  0 front-left  (low)
     W / 2, 0,  L / 2, //  1 front-right (low)
     W / 2, 0, -L / 2, //  2 back-right  (under peak)
    -W / 2, 0, -L / 2, //  3 back-left
    -W / 2, H, -L / 2, //  4 peak-left
     W / 2, H, -L / 2, //  5 peak-right
  ]);

  // Indices — winding chosen so computeVertexNormals points the right way.
  // Outward normal direction is in the comment for each face.
  const idx = new Uint16Array([
    // Slope (top, drivable): normal up-and-toward-+Z
    0, 1, 5,   0, 5, 4,
    // Bottom: normal down (-Y)
    0, 3, 2,   0, 2, 1,
    // Back (vertical at -Z): normal -Z
    3, 4, 5,   3, 5, 2,
    // Left (triangle at -X): normal -X
    0, 4, 3,
    // Right (triangle at +X): normal +X
    1, 2, 5,
  ]);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  geo.computeVertexNormals();

  const mesh = new THREE.Mesh(geo, new THREE.MeshToonMaterial({ color }));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.position.set(position.x, position.y, position.z);
  mesh.rotation.y = yaw;
  scene.add(mesh);

  // Physics: convex hull from same vertex cloud. Rapier auto-derives faces.
  const rb = sim.world.createRigidBody(
    sim.rapier.RigidBodyDesc.fixed()
      .setTranslation(position.x, position.y, position.z)
      .setRotation({
        x: 0,
        y: Math.sin(yaw / 2),
        z: 0,
        w: Math.cos(yaw / 2),
      }),
  );
  const cd = sim.rapier.ColliderDesc.convexHull(verts);
  if (cd) sim.world.createCollider(cd, rb);
  rb.userData = { surface };
}

function addGroundPlane(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: { size: number; color: number; surface?: SurfaceName },
): void {
  const { size, color, surface = 'road' } = opts;
  const geo = new THREE.PlaneGeometry(size, size, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshToonMaterial({ color });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  scene.add(mesh);

  sim.addStaticCuboid({ x: 0, y: -1, z: 0 }, { x: size / 2, y: 1, z: size / 2 }, surface);
}

function addGrid(scene: THREE.Scene, size: number, divisions: number): void {
  const grid = new THREE.GridHelper(size, divisions, 0x4a4f78, 0x21243a);
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.35;
  scene.add(grid);
}

function addPerimeterWalls(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: { halfExtent: number; height?: number; thickness?: number; color?: number },
): void {
  const { halfExtent: R, height = 3, thickness = 1.2, color = 0x4a4f78 } = opts;
  const mat = new THREE.MeshToonMaterial({ color });
  const make = (cx: number, cz: number, w: number, d: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, height, d), mat);
    m.position.set(cx, height / 2, cz);
    m.castShadow = true;
    m.receiveShadow = true;
    scene.add(m);
    sim.addStaticCuboid(
      { x: cx, y: height / 2, z: cz },
      { x: w / 2, y: height / 2, z: d / 2 },
      'wall',
    );
  };
  make( R, 0, thickness, R * 2);
  make(-R, 0, thickness, R * 2);
  make(0,  R, R * 2, thickness);
  make(0, -R, R * 2, thickness);
}

function addBoostStrip(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: { position: Vec3; widthX: number; widthZ: number; yawDeg?: number },
): void {
  const { position, widthX, widthZ, yawDeg = 0 } = opts;
  const yaw = THREE.MathUtils.degToRad(yawDeg);
  const geo = new THREE.PlaneGeometry(widthX, widthZ);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({ color: 0xffd75c });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(position.x, 0.02, position.z);
  mesh.rotation.y = yaw;
  scene.add(mesh);
  // Physics surface pad — slightly raised cuboid so its top is at y=0.
  sim.addStaticCuboid(
    { x: position.x, y: -0.5, z: position.z },
    { x: widthX / 2, y: 0.5, z: widthZ / 2 },
    'boost',
  );
}

function addSurfacePatch(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: { position: Vec3; size: number; color: number; surface: SurfaceName },
): void {
  const { position, size, color, surface } = opts;
  const geo = new THREE.PlaneGeometry(size, size);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshToonMaterial({ color });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(position.x, 0.01, position.z);
  scene.add(mesh);
  sim.addStaticCuboid(
    { x: position.x, y: -0.5, z: position.z },
    { x: size / 2, y: 0.5, z: size / 2 },
    surface,
  );
}

function addBlockPillar(
  scene: THREE.Scene,
  sim: SimWorld,
  opts: { position: Vec3; halfExtents: Vec3; color: number; surface?: SurfaceName },
): void {
  const { position, halfExtents: he, color, surface = 'wall' } = opts;
  const geo = new THREE.BoxGeometry(he.x * 2, he.y * 2, he.z * 2);
  const mesh = new THREE.Mesh(geo, new THREE.MeshToonMaterial({ color }));
  mesh.position.set(position.x, position.y, position.z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  scene.add(mesh);
  sim.addStaticCuboid(position, he, surface);
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRACK 1 — SANDBOX                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

// Compass-cross path. Each ramp gets 3 colinear waypoints (approach → peak →
// landing) so lookahead=1 AI traces them perfectly and launches off every ramp.
const SANDBOX_WAYPOINTS: readonly Vec3[] = [
  // North ramp run
  { x: 0, y: 0.5, z: -15 },
  { x: 0, y: 0.5, z: -36 },   // ON the slope (peak at z=-40)
  { x: 0, y: 0.5, z: -55 },   // landing zone
  // arc toward east
  { x: 30, y: 0.5, z: -30 },
  // East ramp run
  { x: 15, y: 0.5, z: 0 },
  { x: 36, y: 0.5, z: 0 },    // on slope
  { x: 55, y: 0.5, z: 0 },    // landing
  // arc toward south
  { x: 30, y: 0.5, z: 30 },
  // South ramp run
  { x: 0, y: 0.5, z: 15 },
  { x: 0, y: 0.5, z: 36 },
  { x: 0, y: 0.5, z: 55 },
  // arc toward west
  { x: -30, y: 0.5, z: 30 },
  // West ramp run
  { x: -15, y: 0.5, z: 0 },
  { x: -36, y: 0.5, z: 0 },
  { x: -55, y: 0.5, z: 0 },
  // arc back to north start
  { x: -30, y: 0.5, z: -30 },
];

const SANDBOX_SPAWNS: readonly { position: Vec3; yaw: number }[] = [
  { position: { x: 0, y: 1.0, z: 0 }, yaw: 0 },
  { position: { x: 3, y: 1.0, z: 2 }, yaw: 0 },
  { position: { x: -3, y: 1.0, z: 2 }, yaw: 0 },
  { position: { x: 0, y: 1.0, z: 4 }, yaw: 0 },
];

const sandbox: TrackDef = {
  id: 'sandbox',
  name: 'Sandbox',
  biome: 'sandbox',
  difficulty: 1,
  blurb: 'Open arena with every surface and a ramp at each compass point.',
  waypoints: SANDBOX_WAYPOINTS,
  spawns: SANDBOX_SPAWNS,
  build(scene, sim) {
    addGroundPlane(scene, sim, { size: 400, color: 0x2a2d44 });
    addGrid(scene, 200, 40);
    addPerimeterWalls(scene, sim, { halfExtent: 80 });

    // 4 wedge ramps at compass points (low end facing origin / player).
    // Local wedge has LOW at +Z, HIGH at -Z. yawDeg rotates the LOW direction:
    //   0   → LOW at +Z (so player coming from +Z hits low end first)
    //   90  → LOW at +X
    //   180 → LOW at -Z
    //   270 → LOW at -X
    addWedgeRamp(scene, sim, {
      position: { x: 0, y: 0, z: -32 },
      yawDeg: 0, // LOW at +Z (toward origin) — player drives -Z onto slope
      width: 8, length: 16, height: 5,
      color: 0x3a4068,
    });
    addWedgeRamp(scene, sim, {
      position: { x: 32, y: 0, z: 0 },
      yawDeg: 270, // LOW at -X (toward origin) — player drives +X onto slope
      width: 8, length: 16, height: 5,
      color: 0x3a4068,
    });
    addWedgeRamp(scene, sim, {
      position: { x: 0, y: 0, z: 32 },
      yawDeg: 180, // LOW at -Z (toward origin)
      width: 8, length: 16, height: 5,
      color: 0x3a4068,
    });
    addWedgeRamp(scene, sim, {
      position: { x: -32, y: 0, z: 0 },
      yawDeg: 90, // LOW at +X (toward origin)
      width: 8, length: 16, height: 5,
      color: 0x3a4068,
    });

    addBoostStrip(scene, sim, {
      position: { x: 20, y: 0, z: -20 },
      widthX: 4, widthZ: 24,
    });
    addSurfacePatch(scene, sim, {
      position: { x: -25, y: 0, z: 15 },
      size: 14,
      color: 0x9af6ff,
      surface: 'ice',
    });
    addSurfacePatch(scene, sim, {
      position: { x: 25, y: 0, z: 20 },
      size: 14,
      color: 0x8a6b3f,
      surface: 'dirt',
    });

    // Central decorative pillar (gives drift practice around a fixed obstacle).
    addBlockPillar(scene, sim, {
      position: { x: 0, y: 1.5, z: -10 },
      halfExtents: { x: 3, y: 1.5, z: 3 },
      color: 0x4d5a8c,
    });
  },
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRACK 2 — SPEEDWAY  (oval-ish, long straights, boost-friendly)              */
/* ─────────────────────────────────────────────────────────────────────────── */

const SPEEDWAY_WAYPOINTS: readonly Vec3[] = [
  { x:  55, y: 0.5, z:  35 },
  { x:  55, y: 0.5, z: -35 },
  { x:  40, y: 0.5, z: -60 },
  { x:   0, y: 0.5, z: -70 },
  { x: -40, y: 0.5, z: -60 },
  { x: -55, y: 0.5, z: -35 },
  { x: -55, y: 0.5, z:  35 },
  { x: -40, y: 0.5, z:  60 },
  { x:   0, y: 0.5, z:  70 },
  { x:  40, y: 0.5, z:  60 },
];

const SPEEDWAY_SPAWNS: readonly { position: Vec3; yaw: number }[] = [
  { position: { x:  55, y: 1.0, z:  45 }, yaw: Math.PI }, // facing -Z
  { position: { x:  60, y: 1.0, z:  48 }, yaw: Math.PI },
  { position: { x:  52, y: 1.0, z:  48 }, yaw: Math.PI },
  { position: { x:  56, y: 1.0, z:  51 }, yaw: Math.PI },
];

const speedway: TrackDef = {
  id: 'speedway',
  name: 'Neon Speedway',
  biome: 'neon',
  difficulty: 2,
  blurb: 'Long straights, sweeping curves, boost strips on the back stretch.',
  waypoints: SPEEDWAY_WAYPOINTS,
  spawns: SPEEDWAY_SPAWNS,
  build(scene, sim) {
    addGroundPlane(scene, sim, { size: 400, color: 0x161936 });
    addGrid(scene, 240, 48);

    // Outer perimeter — bigger than sandbox.
    addPerimeterWalls(scene, sim, { halfExtent: 90, height: 4, color: 0x2a2855 });

    // Inner "infield" walls that force a racing line around them.
    addBlockPillar(scene, sim, {
      position: { x: 0, y: 1.5, z: 0 },
      halfExtents: { x: 22, y: 1.5, z: 35 },
      color: 0x1a1e3a,
    });

    // 2 boost strips on the long straights.
    addBoostStrip(scene, sim, {
      position: { x: 55, y: 0, z: 0 },
      widthX: 6, widthZ: 32,
    });
    addBoostStrip(scene, sim, {
      position: { x: -55, y: 0, z: 0 },
      widthX: 6, widthZ: 32,
    });

    // Two jump ramps on the curved ends — optional shortcuts if you have the line.
    addWedgeRamp(scene, sim, {
      position: { x: 0, y: 0, z: -60 },
      yawDeg: 0,
      width: 10, length: 14, height: 3.5,
      color: 0xff3ea5,
    });
    addWedgeRamp(scene, sim, {
      position: { x: 0, y: 0, z: 60 },
      yawDeg: 180,
      width: 10, length: 14, height: 3.5,
      color: 0xff3ea5,
    });

    // Light strip "decorations" — visual only, no physics, hint at racing line.
    const stripMat = new THREE.MeshBasicMaterial({ color: 0x00e0ff });
    for (let i = 0; i < 8; i++) {
      const t = i / 8;
      const angle = t * Math.PI * 2;
      const r = 75;
      const stripGeo = new THREE.PlaneGeometry(1.2, 4);
      stripGeo.rotateX(-Math.PI / 2);
      const m = new THREE.Mesh(stripGeo, stripMat);
      m.position.set(Math.cos(angle) * r, 0.02, Math.sin(angle) * r * 0.8);
      m.rotation.y = -angle;
      scene.add(m);
    }
  },
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* TRACK 3 — FUNHOUSE  (technical corridor with rhythm ramps + surface mix)    */
/* ─────────────────────────────────────────────────────────────────────────── */

const FUNHOUSE_WAYPOINTS: readonly Vec3[] = [
  { x:  0, y: 0.5, z: -10 },
  { x:  0, y: 0.5, z: -40 },
  { x: 25, y: 0.5, z: -50 },
  { x: 40, y: 0.5, z: -30 },
  { x: 40, y: 0.5, z:  10 },
  { x: 25, y: 0.5, z:  35 },
  { x:  0, y: 0.5, z:  40 },
  { x: -25, y: 0.5, z:  35 },
  { x: -40, y: 0.5, z:  10 },
  { x: -40, y: 0.5, z: -30 },
  { x: -25, y: 0.5, z: -50 },
];

const FUNHOUSE_SPAWNS: readonly { position: Vec3; yaw: number }[] = [
  { position: { x:  0, y: 1.0, z:  0 }, yaw: Math.PI },
  { position: { x:  3, y: 1.0, z:  3 }, yaw: Math.PI },
  { position: { x: -3, y: 1.0, z:  3 }, yaw: Math.PI },
  { position: { x:  0, y: 1.0, z:  6 }, yaw: Math.PI },
];

const funhouse: TrackDef = {
  id: 'funhouse',
  name: 'Funhouse',
  biome: 'verdant',
  difficulty: 3,
  blurb: 'Rhythm ramps, ice, dirt, and a tight loop. Precision over speed.',
  waypoints: FUNHOUSE_WAYPOINTS,
  spawns: FUNHOUSE_SPAWNS,
  build(scene, sim) {
    addGroundPlane(scene, sim, { size: 300, color: 0x1f3324 });
    addGrid(scene, 200, 40);
    addPerimeterWalls(scene, sim, { halfExtent: 60, color: 0x2b4730 });

    // Rhythm section: 3 close ramps you can chain together.
    // Player faces -Z (yaw=π at spawn) so LOW end must be at +Z → yawDeg=0.
    for (let i = 0; i < 3; i++) {
      addWedgeRamp(scene, sim, {
        position: { x: 0, y: 0, z: -18 - i * 12 },
        yawDeg: 0,
        width: 7, length: 8, height: 3 + i * 0.7,
        color: 0x52a064,
      });
    }

    // Ice corridor on the right side — practice grip.
    addSurfacePatch(scene, sim, {
      position: { x: 35, y: 0, z: -10 },
      size: 22,
      color: 0x9af6ff,
      surface: 'ice',
    });
    addSurfacePatch(scene, sim, {
      position: { x: 35, y: 0, z: 14 },
      size: 18,
      color: 0x9af6ff,
      surface: 'ice',
    });

    // Dirt section on the left.
    addSurfacePatch(scene, sim, {
      position: { x: -35, y: 0, z: -10 },
      size: 22,
      color: 0x8a6b3f,
      surface: 'dirt',
    });
    addSurfacePatch(scene, sim, {
      position: { x: -35, y: 0, z: 14 },
      size: 18,
      color: 0x8a6b3f,
      surface: 'dirt',
    });

    // Boost strip on the back stretch.
    addBoostStrip(scene, sim, {
      position: { x: 0, y: 0, z: 30 },
      widthX: 30, widthZ: 4,
    });

    // Some scattered pillars to thread between.
    addBlockPillar(scene, sim, {
      position: { x: 15, y: 1.5, z: 0 },
      halfExtents: { x: 1.5, y: 1.5, z: 1.5 },
      color: 0x3a6346,
    });
    addBlockPillar(scene, sim, {
      position: { x: -15, y: 1.5, z: 0 },
      halfExtents: { x: 1.5, y: 1.5, z: 1.5 },
      color: 0x3a6346,
    });
    addBlockPillar(scene, sim, {
      position: { x: 0, y: 1.5, z: 22 },
      halfExtents: { x: 6, y: 1.5, z: 1.5 },
      color: 0x3a6346,
    });
  },
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* REGISTRY                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

export const TRACKS: Record<string, TrackDef> = {
  sandbox,
  speedway,
  funhouse,
};

export const TRACK_IDS = ['sandbox', 'speedway', 'funhouse'] as const;
export type TrackId = (typeof TRACK_IDS)[number];

export const DEFAULT_TRACK: TrackId = 'sandbox';
